<template>
    <div class="DebugNodeEditor" v-if="debugNodeActivated">
        <!--Header-->
        <header class="header-editor">
            <div class="centerY justify-space-between" v-if="debugNodeActivated.node.type !== 'DEBUG'">
                <div class="centerY title-editor">
                    <img
                        :src="NodeIconMap[debugNodeActivated.node.type]"
                        alt="icon-node"
                    >
                    <div>{{ debugNodeActivated.node.name }}</div>
                </div>
                <div class="centerY">
                    <v-tooltip left>
                        <template v-slot:activator="{ on }">
                            <div class="btn-icon-custom btn-copy" v-on="on"
                                 @click="copyNodeId(debugNodeActivated.node.id)">
                                <img src="/img/debug/copy.svg" alt="icon-copy-id" style="width: 21px">
                            </div>
                        </template>
                        <span>{{ debugNodeActivated.node.id }}</span>
                    </v-tooltip>
                </div>
            </div>
            <div v-else>
                <div class="title-editor">
                    {{ $f.tc(debugNodeActivated.node.name) }}
                </div>
            </div>
        </header>
        <!--Editor Content-->
        <div class="editor-content fci-scrollbar">
            <!--Message box-->
            <div class="message-box" v-if="debugNodeActivated.node.type !== 'DEBUG'">
                <div v-if="debugNodeActivated.audio">
                    <div class="centerY mb-6 align-self-start" style="flex-wrap: wrap">
                        <strong class="pr-6 audio-name">{{ $f.tc('audioName') }}:</strong>
                        {{ debugNodeActivated.audio.name || 'unknown_audio' }}
                    </div>
                    <audio
                        :src="debugNodeActivated.audio.link"
                        controls="controls"
                        class="audio-custom"
                    ></audio>
                </div>
                <div v-else>
                    <p class="sender">{{ debugNodeActivated.node.type === NodeType.PROCESS ? 'BOT' : $f.tc('user') }}</p>
                    <div class="content">{{ debugNodeActivated.message }}</div>
                </div>
            </div>

            <div
                class="content-item"
                v-if="debugNodeActivated.node.type === NodeType.PROCESS
                || debugNodeActivated.node.type === NodeType.SUPPORTER
                || (debugNodeActivated.node.type === 'DEBUG' && debugNodeActivated.node.name === 'end')"
            >
                <!--Variables monitoring table-->
                <div class="label">{{ $f.tc('variablesMonitoringTable') }}</div>
                <table v-if="monitoringVars && monitoringVars.length > 0">
                    <thead>
                    <tr>
                        <th>{{ $f.tc('variable') }}</th>
                        <th>{{ $f.tc('value') }}</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="variable in monitoringVars" :key="`variable_input_${variable}`">
                        <td>
                          <div class="scroll-auto" style="width: 106px">{{ variable }}</div>
                        </td>
                        <td
                            :style="debugNodeActivated.output[variable] === 'undefined' ? {color: '#9A9A9A', fontStyle: 'italic'} : ''"
                        >
                            <div class="scroll-auto" style="width: 263px">{{ debugNodeActivated.output[variable] }}</div>
                        </td>
                    </tr>
                    </tbody>
                </table>
                <div v-else class="no-data">{{ $f.tc('noVariableMonitoring') }}</div>

                <!--Setting-->
                <div v-if="debugNodeActivated.node.type !== 'DEBUG'">
                    <div class="label mt-12">{{ $f.tc('setting') }}</div>
                    <table v-if="debugNodeActivated.node.setting" class="mt-1">
                        <thead>
                        <tr>
                            <td>{{ $f.tc('key') }}</td>
                            <td>{{ $f.tc('value') }}</td>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="(value, key) in debugNodeActivated.node.setting" :key="key">
                            <td>
                                <div class="scroll-auto" style="width: 106px">{{ key }}</div>
                            </td>
                            <td>
                                <div class="scroll-auto" style="width: 263px">{{ value }}</div>
                            </td>
                        </tr>
                        </tbody>
                    </table>
                    <div v-else class="no-data">{{ $f.tc('noDataAvailable') }}</div>
                </div>
            </div>
            <div
                class="content-item"
                v-if="debugNodeActivated.node.type === NodeType.PROCESS
                || debugNodeActivated.node.type === NodeType.SUPPORTER
                || (debugNodeActivated.node.type === 'DEBUG' && debugNodeActivated.node.name === 'end')"
            >
                <!--JSON input/output-->
                <div class="label">JSON</div>
                <div class="hide-menu">
                    <VueJsonEditor
                        v-model="json"
                        mode="code"
                        :expanded-on-start="false"
                    ></VueJsonEditor>
                </div>
            </div>
            <div
                class="content-item"
                v-if="debugNodeActivated.node.type === NodeType.RE_PROMPT
                 || debugNodeActivated.node.type === NodeType.DECISION
                 || debugNodeActivated.node.type === NodeType.FALLBACK"
            >
                <!--NLP Response-->
                <div class="label">NLP Response</div>
                <table v-if="debugNodeActivated.nlpResponse" class="mt-1">
                    <tbody>
                    <tr>
                        <td>{{ $f.tc('statusCode') }}</td>
                        <td
                            class="text-right"
                            style="font-family: 'open sans semi',serif;"
                            :style="{color: `${debugNodeActivated.nlpResponse.status.code === 200 ? '#66BB6A' : '#DE6F5F'}`}"
                        >
                            {{ debugNodeActivated.nlpResponse.status.code }}
                        </td>
                    </tr>
                    <tr>
                        <td>{{ $f.tc('latency') }}</td>
                        <td class="text-right">{{ debugNodeActivated.nlpResponse.latency }}ms</td>
                    </tr>
                    <tr>
                        <td>{{ $f.tc('intent') }}</td>
                        <td class="text-right"
                            :style="debugNodeActivated.nlpResponse.data.intents ? '' : {color: '#9A9A9A', fontStyle: 'italic'}">
                            {{
                                debugNodeActivated.nlpResponse.data.intents ? debugNodeActivated.nlpResponse.data.intents[0].label : $t('empty')
                            }}
                        </td>
                    </tr>
                    </tbody>
                </table>
                <div v-else class="no-data">{{ $f.tc('noDataAvailable') }}</div>

                <!--Conditions-->
                <div class="label mt-12">{{ $f.tc('condition') }}</div>
                <div
                    v-for="(conditions, idx) in debugNodeActivated.node.conditions"
                    class="conditions-item"
                    :key="`conditions_${idx}`"
                >
                    <div v-if="idx !== 0" class="or-clause mb-6">OR</div>
                    <ConditionsForm :value="convertConditions(conditions)" :readonly="true"
                                    :class="idx === 0 ? 'mb-6': ''"/>
                </div>
                <div
                    v-if="!debugNodeActivated.node.conditions || debugNodeActivated.node.conditions.length === 0"
                    class="no-data"
                >
                    {{ $f.tc('noDataAvailable') }}
                </div>
            </div>
            <div
                class="content-item"
                v-if="debugNodeActivated.node.type === NodeType.SUPPORTER"
            >
                <!--Phone list-->
                <div class="label">{{ $f.tc('phoneList') }}</div>
                <table
                    v-if="debugNodeActivated.node.phone_list && debugNodeActivated.node.phone_list.length > 0"
                    class="mt-1"
                >
                    <thead>
                    <tr>
                        <td>{{ $f.tc('name') }}</td>
                        <td>{{ $f.tc('phone') }}</td>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(phone, idx) in debugNodeActivated.node.phone_list" :key="`${phone.value}_${idx}`">
                        <td>{{ phone.key }}</td>
                        <td>{{ phone.value }}</td>
                    </tr>
                    </tbody>
                </table>
                <div v-else class="no-data">{{ $f.tc('noDataAvailable') }}</div>

                <!--Transferred Number-->
                <div class="label mt-12">{{ $f.tc('transferredNumber') }}</div>
                <table
                    v-if="debugNodeActivated.node.phone_list && debugNodeActivated.node.phone_list.length > 0"
                    class="mt-1"
                >
                    <thead>
                    <tr>
                        <td>Key</td>
                        <td>Value</td>
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                        <td>$transfer_call_number</td>
                        <td>{{ debugNodeActivated.transferCallNumber }}</td>
                    </tr>
                    </tbody>
                </table>
                <div v-else class="no-data">{{ $f.tc('noDataAvailable') }}</div>
            </div>
            <div
                class="content-item"
                v-if="debugNodeActivated.cardLogs
                 && debugNodeActivated.node.card_list
                 && debugNodeActivated.cardLogs.length > 0"
            >
                <!--Detail latency for cards-->
                <div class="label">{{ $f.tc('cardsLatency') }}</div>
                <table class="mt-8">
                    <tbody>
                    <tr v-for="(cardLog, idxCard) in debugNodeActivated.cardLogs" :key="`${idxCard}_${cardLog.index}`">
                        <td>
                            <v-tooltip left>
                                <template v-slot:activator="{ on }">
                                    <div class="centerY" v-on="on" style="cursor: default">
                                        <img
                                            class="mr-6"
                                            :src="cardLog.error ? '/img/debug/fail.png': '/img/debug/success.png'"
                                            :alt="`icon-status-card-${cardLog.index}`"
                                            style="width: 16px"
                                        >
                                        {{ debugNodeActivated.node.card_list[cardLog.index].name }}
                                    </div>
                                </template>
                                <div style="max-width: 300px">
                                    {{ cardLog.error ? cardLog.error : $f.tc('success') }}
                                </div>
                            </v-tooltip>
                        </td>
                        <td class="text-right">{{ cardLog.latency }}ms</td>
                    </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>
<script>
import ScenarioService from "@/services/scenario.service";
import VueJsonEditor from 'vue-json-editor';
import {mapState} from "vuex";
import ConditionsForm from "@/components/ConditionsForm.vue";

const {NodeType, NodeIconMap} = ScenarioService
export default {
    name: 'DebugNodeEditor',
    components: {
        ConditionsForm,
        VueJsonEditor
    },
    data() {
        return {
            NodeType,
            NodeIconMap,
            variablesInput: {},
            variablesOutput: {},
            latencyCards: []
        };
    },
    computed: {
        ...mapState({
            monitoringVars: state => state.debug.monitoringVariables,
            debugNodeActivated: state => state.debug.debugNodeActivated,
        }),
        json() {
            return {
                input: this.$utils.sortPropertiesObject(this.debugNodeActivated.input),
                output: this.$utils.sortPropertiesObject(this.debugNodeActivated.output),
                nlp_response: this.$utils.sortPropertiesObject(this.debugNodeActivated.nlpResponse),
            }
        }
    },
    methods: {
        close() {
            this.$emit('close');
        },
        copyNodeId(nodeId) {
            this.$utils.copyToClipboard(nodeId, this.$f.tc('copyNodeIdSuccess'))
        },
        convertConditions(conditions) {
            let conditionsData = []
            if (typeof conditions === 'object') {
                Object.keys(conditions).forEach(key => {
                    if (key !== '_key') {
                        conditionsData.push(conditions[key])
                    }
                })
            }
            return conditionsData
        }
    }
}
</script>
<style lang="scss" scoped>
.DebugNodeEditor {
    width: 100%;
    height: 100%;
    background-color: #fff;
    box-shadow: rgba(149, 157, 165, 0.2) 1.95px 8px 18px;
    font-family: "open sans", serif;

    .no-data {
        color: #919191;
        font-size: 13px;
    }

    .scroll-auto {
        height: 100%;
        overflow-x: auto;
    }

    .input-custom {
        ::v-deep {
            .v-input__slot {
                font-family: "open sans", serif !important;
                font-size: 14px !important;
                height: 32px;
                background: #FFFFFF;
                border-radius: 5px;
                border: 1px solid #D9D9D9;
                outline: none;
                padding: 6px 6px 6px 12px;
                margin: 0;
                display: flex;
                align-items: center;
                position: relative;

                &:before, &:after {
                    border: none !important;
                }
            }
        }
    }

    .btn-custom {
        img {
            width: 17px;
        }
    }

    .label {
        margin-bottom: 2px !important;
    }

    .hide-menu {
        ::v-deep .jsoneditor-menu {
            display: none;
        }
    }

    .or-clause {
        background: #EEF6FF;
        color: #0052CC;
        border: 1px solid #0052CC;
        font-size: 12px;
        font-family: "open sans semi", serif;
        border-radius: 5px;
        padding: 2px 12px;
    }

    table {
        width: 100%;
        border-collapse: collapse;
        font-family: "open sans", serif;
        font-size: 13px;

        thead {
            font-family: "open sans semi", serif;
            background: #F0F0F0;

            th {
                text-align: left;
            }
        }

        tr {
            height: 36px;
            border: 1px solid #D9D9D9;
        }

        th, td {
            padding: 8px 12px;
        }
    }

    .header-editor {
        padding: 8px 12px;
        border-bottom: 1px solid #EFEFEF;
        z-index: 1;

        .title-editor {
            font-size: 15px;
            font-family: "open sans semi", serif;
            color: #000;

            img {
                width: 18px;
                height: 18px;
                margin-right: 8px;
            }
        }

        .btn-copy {
            img {
                transform: scale(0.9);
            }
        }
    }

    .editor-content {
        height: calc(100vh - 215px);
        overflow-y: auto;
        overflow-x: hidden;

        .content-item {
            padding: 12px;
            border-bottom: 1px solid #EFEFEF;
        }

        .content-tab {
            padding: 12px;
        }

        .conditions-item {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
        }

        .message-box {
            border-radius: 5px;
            border: 1px dashed #D9D9D9;
            background: #F5F5F5;
            padding: 12px;
            margin: 12px;

            .sender {
                font-size: 12px;
                line-height: 20px;
                color: #8C8C8C;
                margin-bottom: 8px !important;
                font-family: "open sans semi", serif;
                text-transform: capitalize;
            }

            .content {
                color: #262626;
                font-size: 14px;
                line-height: 21px;
                width: 392px !important;
                overflow-x: auto;

                &::selection {
                    background: #FFFFFF;
                }
            }
        }
    }
    
    .audio-name {
        font-size: 12px;
        color: #8C8C8C;
        font-family: "open sans semi", serif;
    }
    
    .audio-custom {
        display: block;
        width: 100%;
        border: 1px solid #D9D9D9;
        border-radius: 5px;
        background: #f1f3f4;
    }
}

::v-deep {
    .jsoneditor-vue {
        height: 350px !important;
    }

    div.jsoneditor-outer {
        padding: 0 !important;
        margin: 0 !important;
    }

    .ace_scroller {
        width: 361px !important;
    }

    .ace_scrollbar, .ace_scrollbar-inner {
        width: 7px !important;
    }
}
</style>
